import React, { useEffect, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ApplyButton from './ApplyButton';
interface StatCardProps {
  percentage?: number;
  prefix?: string;
  value: string;
  description: string;
  delay: number;
}
const StatCard = ({
  percentage,
  prefix,
  value,
  description,
  delay
}: StatCardProps) => {
  const [count, setCount] = useState(0);
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });
  useEffect(() => {
    if (inView) {
      controls.start({
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.7,
          delay
        }
      });
      if (percentage) {
        let startTimestamp: number;
        const duration = 2000;
        const step = (timestamp: number) => {
          if (!startTimestamp) startTimestamp = timestamp;
          const progress = Math.min((timestamp - startTimestamp) / duration, 1);
          setCount(Math.floor(progress * percentage));
          if (progress < 1) {
            window.requestAnimationFrame(step);
          }
        };
        window.requestAnimationFrame(step);
      }
    }
  }, [inView, controls, percentage, delay]);
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 50
  }} animate={controls} className="w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1rem)] mb-6" data-id="element-211">
      <div className="h-full bg-gradient-to-b from-[#111111] to-[#0a0a0a] border border-gray-800 rounded-lg p-6 shadow-xl flex flex-col justify-between" data-id="element-212">
        <div data-id="element-213">
          <h3 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-214">
            {prefix && <span className="text-[#ffdf07]" data-id="element-215">{prefix}</span>}
            {percentage && <span className="text-[#ffdf07]" data-id="element-216">
                {count}
                <span data-id="element-217">%</span>
              </span>}
            {!percentage && <span className="text-[#ffdf07]" data-id="element-218">{value}</span>}
          </h3>
          <p className="text-gray-300 libre-caslon-text-regular leading-relaxed" data-id="element-219">
            {description}
          </p>
        </div>
      </div>
    </motion.div>;
};
interface StatsSectionHealthcareProps {
  scrollToCalendar?: () => void;
}
const StatsSectionHealthcare = ({
  scrollToCalendar
}: StatsSectionHealthcareProps) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const controls = useAnimation();
  useEffect(() => {
    if (inView) {
      controls.start({
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.7
        }
      });
    }
  }, [inView, controls]);
  return <section className="w-full bg-black py-20 px-4" data-id="element-220">
      <div className="max-w-5xl mx-auto" data-id="element-221">
        <motion.div ref={ref} initial={{
        opacity: 0,
        y: 20
      }} animate={controls} className="text-center mb-14" data-id="element-222">
          <h2 className="text-white text-3xl md:text-4xl mb-6 typekit-heading" data-id="element-223">
            The Hard Numbers: Why This Matters
          </h2>
        </motion.div>
        <div className="flex flex-wrap justify-center gap-4" data-id="element-224">
          <StatCard percentage={98} description="of healthcare clinic owners in your revenue range will never exit for what their business is truly worth." delay={0.1} data-id="element-225" />
          <StatCard percentage={82} description="of owners fly blind into a sale without a professional valuation." delay={0.2} data-id="element-226" />
          <StatCard percentage={72} description="of owners leave six or seven figures on the table because they are unprepared." delay={0.3} data-id="element-227" />
          <StatCard percentage={80} description="of healthcare deals that fail are due to poorly organized financials." delay={0.4} data-id="element-228" />
        </div>
        <div className="mt-12" data-id="element-229">
          {scrollToCalendar ? <div className="flex justify-center" data-id="element-230">
              <div className="relative" data-id="element-231">
                <div className="button-border-light" data-id="element-232"></div>
                <motion.button onClick={scrollToCalendar} whileHover={{
              scale: 1.05,
              boxShadow: '0px 8px 20px rgba(255, 223, 7, 0.3)'
            }} className="relative z-10 bg-gradient-to-r from-[#ffdf07] via-[#fae651] to-[#ffdf07] text-black font-semibold py-3 px-8 sm:px-10 rounded-md text-base shadow-[0_4px_14px_rgba(255,223,7,0.25)] border border-[#ffd70077] transition-all duration-300" data-id="element-233">
                  Book My Strategy Call
                </motion.button>
              </div>
            </div> : <ApplyButton data-id="element-234" />}
        </div>
      </div>
    </section>;
};
export default StatsSectionHealthcare;